import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CallQueueWidget = ({ onAcceptCall }) => {
  const [queueData, setQueueData] = useState({
    waitingCalls: 3,
    averageWaitTime: 125,
    longestWaitTime: 245,
    totalCallsToday: 47
  });

  const [waitingCalls] = useState([
    {
      id: 1,
      customerName: "Ahmet Yılmaz",
      phoneNumber: "+90 532 123 4567",
      waitTime: 245,
      priority: "Yüksek",
      callType: "Destek"
    },
    {
      id: 2,
      customerName: "Fatma Demir", 
      phoneNumber: "+90 533 987 6543",
      waitTime: 180,
      priority: "Orta",
      callType: "Satış"
    },
    {
      id: 3,
      customerName: "Mehmet Kaya",
      phoneNumber: "+90 534 555 7890", 
      waitTime: 95,
      priority: "Düşük",
      callType: "Bilgi"
    }
  ]);

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setQueueData(prev => ({
        ...prev,
        averageWaitTime: prev?.averageWaitTime + Math.floor(Math.random() * 10 - 5),
        longestWaitTime: prev?.longestWaitTime + Math.floor(Math.random() * 15 - 5)
      }));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const formatWaitTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Yüksek':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Orta':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Düşük':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCallTypeIcon = (callType) => {
    switch (callType) {
      case 'Destek':
        return 'HelpCircle';
      case 'Satış':
        return 'ShoppingCart';
      case 'Bilgi':
        return 'Info';
      default:
        return 'Phone';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-subtle border border-slate-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Arama Kuyruğu</h3>
          <p className="text-sm text-slate-600">Bekleyen aramalar ve istatistikler</p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-success rounded-full pulse-slow" />
          <span className="text-sm font-medium text-slate-900">Canlı</span>
        </div>
      </div>
      {/* Queue Statistics */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-slate-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-slate-900 mb-1">
            {queueData?.waitingCalls}
          </div>
          <div className="text-sm text-slate-600">Bekleyen Arama</div>
        </div>
        
        <div className="bg-slate-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-slate-900 mb-1">
            {formatWaitTime(queueData?.averageWaitTime)}
          </div>
          <div className="text-sm text-slate-600">Ortalama Bekleme</div>
        </div>
        
        <div className="bg-slate-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-slate-900 mb-1">
            {formatWaitTime(queueData?.longestWaitTime)}
          </div>
          <div className="text-sm text-slate-600">En Uzun Bekleme</div>
        </div>
        
        <div className="bg-slate-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-slate-900 mb-1">
            {queueData?.totalCallsToday}
          </div>
          <div className="text-sm text-slate-600">Bugünkü Toplam</div>
        </div>
      </div>
      {/* Waiting Calls List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-slate-900">Bekleyen Aramalar</h4>
          <Button
            variant="outline"
            size="xs"
            iconName="RotateCcw"
          >
            Yenile
          </Button>
        </div>

        {waitingCalls?.length > 0 ? (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {waitingCalls?.map((call) => (
              <div
                key={call?.id}
                className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 transition-colors duration-150"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Icon name={getCallTypeIcon(call?.callType)} size={16} className="text-slate-500" />
                    <span className="font-medium text-slate-900">{call?.customerName}</span>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(call?.priority)}`}>
                    {call?.priority}
                  </span>
                </div>
                
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-slate-600">{call?.phoneNumber}</span>
                  <div className="flex items-center space-x-1 text-sm text-slate-600">
                    <Icon name="Clock" size={14} />
                    <span>{formatWaitTime(call?.waitTime)}</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                    {call?.callType}
                  </span>
                  <Button
                    variant="success"
                    size="xs"
                    onClick={() => onAcceptCall(call)}
                    iconName="Phone"
                    iconPosition="left"
                  >
                    Kabul Et
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <Icon name="Phone" size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-sm text-slate-600">Şu anda bekleyen arama yok</p>
          </div>
        )}
      </div>
      {/* Quick Actions */}
      <div className="mt-6 pt-4 border-t border-slate-200">
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            size="sm"
            iconName="Settings"
            iconPosition="left"
          >
            Kuyruk Ayarları
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="BarChart3"
            iconPosition="left"
          >
            İstatistikler
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CallQueueWidget;